import { EventModel } from 'src_common/common/websocket2/models/EventModel';
import { action, computed, observable, makeAutoObservable } from 'mobx';
import { SdkStaff } from 'src/AppState/sdk/staff';
import { EventId } from 'src_common/common/websocket2/id/WebsocketId';

const TIMELINE_STATUS = ['prematch', 'show', 'started', 'finished'];

interface PostBodyType {
    timeSettings?: {
        startTime?: string;
        timeZone?: string;
        timeline?: string;
        tradedInPlay?: boolean;
        offAtStartTime?: boolean;
    };
    autoTakeDown?: boolean;
    inPlaySettings?: {
        tradedInPlay?: boolean;
        betDelay?: number;
    };
}

export class TimeEventSettingsState {
    @observable public isLoadingEventUpdate: boolean = false;
    @observable private betDelayInputValue: string | null = null;

    public constructor(
        private readonly eventIdInner: () => EventId,
        private readonly sdkStaff: SdkStaff
    ) {
        makeAutoObservable(this);
    }
    @computed private get eventId(): EventId {
        return this.eventIdInner();
    }

    @computed private get eventModel(): EventModel | null {
        return this.eventId.getEventModel();
    }

    @computed public get startTime(): string | null {
        return this.eventModel?.timeSettingsStartTime ?? null;
    }

    @computed public get timeline(): string | undefined {
        const timeline = this.eventModel?.timeSettingsTimeline.toLowerCase() ?? '';
        return TIMELINE_STATUS.includes(timeline) ? timeline : '';
    }

    @computed public get tradedInPlay(): boolean | undefined {
        return this.eventModel?.timeSettingsTradedInPlay;
    }

    @computed public get offAtStartTime(): boolean | undefined {
        return this.eventModel?.timeSettingsOffAtStartTime ?? undefined;
    }

    @computed public get autoTakeDown(): boolean | undefined {
        return this.eventModel?.autoTakeDown ?? undefined;
    }

    @computed public get betDelay(): string | undefined {
        if (this.betDelayInputValue === null) {
            return this.eventModel?.inPlaySettingsBetDelay?.toString() ?? undefined;
        }

        return this.betDelayInputValue;
    }

    public get TIMELINE_STATUS(): Array<string> {
        return TIMELINE_STATUS;
    }

    @action public onChangeDate = async (startTime: string | null): Promise<void> => {
        if (startTime === null || startTime === '') {
            return;
        }

        const body = {
            timeSettings: {
                startTime,
            },
        };

        await this.performUpdate(body);
    };

    @action public onChangeTimeZone = async (): Promise<void> => {
        const body = {
            timeSettings: {
                timeZone: 'UTC',
            },
        };

        await this.performUpdate(body);
    };

    @action public onChangeTimeline = async (e: React.ChangeEvent<HTMLSelectElement>): Promise<void> => {
        const timeline = e.currentTarget.value;

        const body = {
            timeSettings: {
                timeline,
            },
        };

        await this.performUpdate(body);
    };

    @action public onChangeTradedInPlay = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const tradedInPlay = e.currentTarget.checked;

        const body = {
            timeSettings: {
                tradedInPlay,
            },
        };

        await this.performUpdate(body);
    };

    @action public onChangeOffAtStartTime = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const offAtStartTime = e.currentTarget.checked;

        const body = {
            timeSettings: {
                offAtStartTime,
            },
        };

        await this.performUpdate(body);
    };

    @action public onChangeAutoTakeDown = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const autoTakeDown = e.currentTarget.checked;

        const body = {
            autoTakeDown,
        };

        await this.performUpdate(body);
    };

    @action public onChangeBetDelay = (e: React.ChangeEvent<HTMLInputElement>): void => {
        this.betDelayInputValue = e.currentTarget.value;
    };

    @action public onChangeBetDelaySave = async (): Promise<void> => {
        if (this.betDelayInputValue !== null) {
            const betDelay = parseInt(this.betDelayInputValue, 10);

            if (betDelay >= 0) {
                const body = {
                    inPlaySettings: {
                        tradedInPlay: true,
                        betDelay,
                    },
                };

                await this.performUpdate(body);
            }
        }
    };

    @action private performUpdate = async (body: PostBodyType): Promise<void> => {
        this.isLoadingEventUpdate = true;
        await this.sdkStaff.updateEvent({ event_id: this.eventId.toOldId(), requestBody: body });
        this.isLoadingEventUpdate = false;
    };
}
